import { DaumPostcodeResponse, DeliveryArea } from '@gooduncles/gu-app-schema';
import { Alert, Button, Drawer, Tag, notification } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { AuditLogger } from 'src/utils/audit-logger';
import { updateCommerceConf } from 'src/utils/firebase/firebase-short-cut';

import { useCommerceConf } from 'src/redux/hooks/useCommerceConf';

import classes from './DeliveryAreaConfig.module.scss';

const daum = (window as any).daum;
const auditLogger = AuditLogger.getInstance();

const DeliveryAreaConfig: FC = () => {
  const { commerceConf } = useCommerceConf();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [postcodeRef, setPostcodeRef] = useState<HTMLDivElement | null>(null);

  const deliveryAreas = commerceConf?.deliveryAreas ?? [];

  const groupedAreas = useMemo(() => {
    const groups = deliveryAreas.reduce<Record<string, DeliveryArea[]>>((acc, area) => {
      const list = acc[area.sido] ?? [];
      return { ...acc, [area.sido]: [...list, area] };
    }, {});
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [deliveryAreas]);

  const handleAddArea = useCallback(
    async (data: DaumPostcodeResponse) => {
      const newArea: DeliveryArea = {
        sigunguCode: String(data.sigunguCode),
        sido: data.sido,
        sigungu: data.sigungu,
      };

      const isDuplicate = deliveryAreas.some((area) => area.sigunguCode === newArea.sigunguCode);
      if (isDuplicate) {
        notification.warning({
          message: '이미 추가된 지역입니다.',
          description: `${newArea.sido} ${newArea.sigungu}`,
        });
        return;
      }

      const updatedAreas = [...deliveryAreas, newArea].sort((a, b) => a.sigunguCode.localeCompare(b.sigunguCode));

      try {
        await updateCommerceConf({ deliveryAreas: updatedAreas });
        notification.success({
          message: '배송 가능 지역이 추가되었습니다.',
          description: `${newArea.sido} ${newArea.sigungu}`,
        });
        auditLogger.log({
          action: 'UPDATE',
          entity: 'config',
          entityId: 'commerceConf',
          summary: `배송 가능 지역 추가: ${newArea.sido} ${newArea.sigungu}`,
          slack: { channel: '4-앱-알림' },
        });
      } catch (error) {
        console.error(error);
        notification.error({ message: '에러 발생', description: `${error}` });
      }
    },
    [deliveryAreas],
  );

  const handleRemoveArea = useCallback(
    async (targetCode: string) => {
      const targetArea = deliveryAreas.find((area) => area.sigunguCode === targetCode);
      const updatedAreas = deliveryAreas.filter((area) => area.sigunguCode !== targetCode);

      try {
        await updateCommerceConf({ deliveryAreas: updatedAreas });
        notification.success({ message: '배송 가능 지역이 삭제되었습니다.' });
        auditLogger.log({
          action: 'DELETE',
          entity: 'config',
          entityId: 'commerceConf',
          summary: `배송 가능 지역 삭제: ${targetArea?.sido} ${targetArea?.sigungu}`,
          slack: { channel: '4-앱-알림' },
        });
      } catch (error) {
        console.error(error);
        notification.error({ message: '에러 발생', description: `${error}` });
      }
    },
    [deliveryAreas],
  );

  useEffect(() => {
    if (daum && postcodeRef && drawerVisible) {
      const postcode = new daum.Postcode({
        onsearch: (data: { q: string }) => setSearchKeyword(data.q),
        oncomplete: handleAddArea,
        width: '100%',
        height: '100%',
        autoClose: false,
      });

      postcode.embed(postcodeRef, searchKeyword ? { q: searchKeyword } : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcodeRef, handleAddArea, drawerVisible]);

  if (!commerceConf) {
    return <div>현재 설정을 불러오지 못했습니다.</div>;
  }

  return (
    <div className={classes.container}>
      <h2>배송 가능 지역</h2>
      <div className={classes.content}>
        <div className={classes.header}>
          <span>총 {deliveryAreas.length}개 지역</span>
          <Button type='primary' onClick={() => setDrawerVisible(true)}>
            지역 추가
          </Button>
        </div>
        {deliveryAreas.length === 0 ? (
          <div className={classes.emptyText}>등록된 배송 가능 지역이 없습니다.</div>
        ) : (
          <div className={classes.groupList}>
            {groupedAreas.map(([sido, areas]) => (
              <div key={sido} className={classes.group}>
                <div className={classes.groupLabel}>{sido}</div>
                <div className={classes.areaList}>
                  {areas.map((area) => (
                    <Tag
                      key={area.sigunguCode}
                      closable
                      className={classes.areaTag}
                      onClose={() => handleRemoveArea(area.sigunguCode)}
                    >
                      {area.sigungu}
                    </Tag>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Drawer
        title='배송 가능 지역 추가'
        open={drawerVisible}
        placement='right'
        width='400px'
        keyboard={true}
        onClose={() => setDrawerVisible(false)}
      >
        <Alert
          message='추가할 지역(구/시/군) 내 아무 주소를 검색하여 선택하면 해당 지역이 자동으로 추가됩니다.'
          type='info'
          showIcon
          style={{ marginBottom: 12 }}
        />
        <div ref={setPostcodeRef} style={{ height: 'calc(100% - 60px)' }} />
      </Drawer>
    </div>
  );
};

export default DeliveryAreaConfig;
